import { useCallback, useMemo, useState } from "react";
import { Box, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { SecureVolumeContainer } from "../../../../../store/types/secureVolume.types";
import { VolumeTypeEnum } from "../../../../../store/types/secureVolume.types";
import { VOLUME_TYPE_CONFIG, fmt } from "../types";

interface VolumeChartProps {
  facilities: SecureVolumeContainer[];
  years: number[];
  inputYear?: number;
  inputValues: Record<string, string>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MonthEntry = Record<string, any>;

interface SecuredLineConfig {
  key: string;
  label: string;
  color: string;
  year: number;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];
const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const SECURED_LINE_COLORS = ["#4caf50", "#9c27b0", "#e91e63", "#00bcd4", "#ff5722", "#795548"];

// --- Helpers ---

function getInputTotal(
  facilityIndices: number[],
  inputValues: Record<string, string>,
  year: number,
  quarter: number,
): number {
  return facilityIndices.reduce((sum, fi) => {
    const key = `f${fi}-${year}-${quarter}`;
    return sum + (Number(inputValues[key]) || 0);
  }, 0);
}

function getMonthDetailSum(
  facilities: SecureVolumeContainer[],
  year: number,
  quarter: number,
  monthNum: number,
  volumeTypes: VolumeTypeEnum[],
): number | null {
  let total = 0;
  let hasAny = false;
  for (const f of facilities) {
    for (const v of f.QuarterVolumes) {
      if (v.Year !== year || v.Quarter !== quarter) continue;
      if (!v.MonthDetails?.length) continue;
      for (const md of v.MonthDetails) {
        if (md.Month === monthNum && volumeTypes.includes(md.VolumeType)) {
          total += md.Quantity;
          hasAny = true;
        }
      }
    }
  }
  return hasAny ? total : null;
}

function distributeQuarterToMonth(quarterTotal: number, month: number): number {
  const q = Math.floor(month / 3);
  const m0 = q * 3;
  const daysInQuarter = DAYS_PER_MONTH[m0] + DAYS_PER_MONTH[m0 + 1] + DAYS_PER_MONTH[m0 + 2];
  return Math.round((quarterTotal * DAYS_PER_MONTH[month]) / daysInQuarter);
}

function getMonthValue(
  facilities: SecureVolumeContainer[],
  year: number,
  month: number,
  volumeType: VolumeTypeEnum,
): number {
  const q = Math.floor(month / 3) + 1;
  const monthNum = month + 1;
  const allVolumes = facilities.flatMap((f) => f.QuarterVolumes);

  const detail = getMonthDetailSum(facilities, year, q, monthNum, [volumeType]);
  if (detail !== null) return detail;

  const quarterTotal = allVolumes
    .filter((v) => v.Year === year && v.Quarter === q && v.VolumeType === volumeType)
    .reduce((s, v) => s + v.Quantity, 0);
  return distributeQuarterToMonth(quarterTotal, month);
}

function getMonthConsumptionForecast(
  facilities: SecureVolumeContainer[],
  year: number,
  month: number,
): { consumption: number; forecast: number } {
  const q = Math.floor(month / 3) + 1;
  const monthNum = month + 1;
  const allVolumes = facilities.flatMap((f) => f.QuarterVolumes);

  const cd = getMonthDetailSum(facilities, year, q, monthNum, [VolumeTypeEnum.Consumption]);
  const fd = getMonthDetailSum(facilities, year, q, monthNum, [VolumeTypeEnum.Forecast]);

  if (cd !== null || fd !== null) {
    return { consumption: cd ?? 0, forecast: fd ?? 0 };
  }

  const sumQ = (type: VolumeTypeEnum) =>
    allVolumes
      .filter((v) => v.Year === year && v.Quarter === q && v.VolumeType === type)
      .reduce((s, v) => s + v.Quantity, 0);

  return {
    consumption: distributeQuarterToMonth(sumQ(VolumeTypeEnum.Consumption), month),
    forecast: distributeQuarterToMonth(sumQ(VolumeTypeEnum.Forecast), month),
  };
}

// Säkrad volym är slutgiltig – har anläggningen säkrat målåret visas den istället för pågående
function isFacilityYearSecured(facility: SecureVolumeContainer, year: number): boolean {
  return facility.QuarterVolumes.some(
    (v) => v.Year === year && v.VolumeType === VolumeTypeEnum.Secured && v.Quantity > 0,
  );
}

function getSecuredMonthValue(
  facilities: SecureVolumeContainer[],
  facilityIndices: number[],
  year: number,
  month: number,
  isLastYear: boolean,
  inputYear: number | undefined,
  inputValues: Record<string, string>,
): number {
  const q = Math.floor(month / 3) + 1;
  const monthNum = month + 1;

  // Check for input values on target year
  if (isLastYear && year === inputYear) {
    const inputTotal = getInputTotal(facilityIndices, inputValues, year, q);
    const hasInput = inputTotal > 0 || Object.keys(inputValues).some((k) =>
      k.startsWith("f") && k.includes(`-${year}-${q}`)
    );
    if (hasInput) {
      return distributeQuarterToMonth(inputTotal, month);
    }
  }

  // Per anläggning: Secured om säkrad, annars InProgress för målåret
  return facilities.reduce((sum, f) => {
    const volumeType = isLastYear && !isFacilityYearSecured(f, year)
      ? VolumeTypeEnum.InProgress
      : VolumeTypeEnum.Secured;

    const detail = getMonthDetailSum([f], year, q, monthNum, [volumeType]);
    if (detail !== null) return sum + detail;

    const quarterTotal = f.QuarterVolumes
      .filter((v) => v.Year === year && v.Quarter === q && v.VolumeType === volumeType)
      .reduce((s, v) => s + v.Quantity, 0);
    return sum + distributeQuarterToMonth(quarterTotal, month);
  }, 0);
}

// --- Build chart data ---

function buildMonthlyData(
  facilities: SecureVolumeContainer[],
  facilityIndices: number[],
  currentYear: number,
  prevYear1: number,
  prevYear2: number,
  securedLines: SecuredLineConfig[],
  allYears: number[],
  inputYear: number | undefined,
  inputValues: Record<string, string>,
  showCurrentYear: boolean,
  showPrevYear1: boolean,
  showPrevYear2: boolean,
  visibleSecured: Set<string>,
): MonthEntry[] {
  const lastYear = allYears[allYears.length - 1];
  const data: MonthEntry[] = [];

  for (let month = 0; month < 12; month++) {
    const entry: MonthEntry = { name: MONTH_NAMES[month] };

    if (showCurrentYear) {
      const { consumption, forecast } = getMonthConsumptionForecast(facilities, currentYear, month);
      entry.currentConsumption = consumption;
      entry.currentForecast = forecast;
    }

    if (showPrevYear1) {
      entry.prev1Consumption = getMonthValue(facilities, prevYear1, month, VolumeTypeEnum.Consumption);
    }

    if (showPrevYear2) {
      entry.prev2Consumption = getMonthValue(facilities, prevYear2, month, VolumeTypeEnum.Consumption);
    }

    for (const line of securedLines) {
      if (visibleSecured.has(line.key)) {
        entry[line.key] = getSecuredMonthValue(
          facilities, facilityIndices, line.year, month,
          line.year === lastYear, inputYear, inputValues,
        );
      }
    }

    data.push(entry);
  }

  return data;
}

// --- Component ---

export function VolumeChart({
  facilities,
  years,
  inputYear,
  inputValues,
}: VolumeChartProps) {
  const [selectedFacilityIndex, setSelectedFacilityIndex] = useState<number | null>(null);
  const [showCurrentYear, setShowCurrentYear] = useState(true);
  const [showPrevYear1, setShowPrevYear1] = useState(true);
  const [showPrevYear2, setShowPrevYear2] = useState(false);
  const [visibleSecured, setVisibleSecured] = useState<Set<string>>(() => new Set());

  const currentYear = new Date().getFullYear();
  const prevYear1 = currentYear - 1;
  const prevYear2 = currentYear - 2;
  const lastYear = years[years.length - 1];

  const activeFacilities = useMemo(() => {
    if (selectedFacilityIndex !== null) return [facilities[selectedFacilityIndex]];
    return facilities;
  }, [facilities, selectedFacilityIndex]);

  const activeFacilityIndices = useMemo(() => {
    if (selectedFacilityIndex !== null) return [selectedFacilityIndex];
    return facilities.map((_, i) => i);
  }, [facilities, selectedFacilityIndex]);

  // Discover which years have Secured or InProgress data
  const securedLines = useMemo<SecuredLineConfig[]>(() => {
    const allVolumes = activeFacilities.flatMap((f) => f.QuarterVolumes);
    const securedYears = new Set<number>();

    for (const v of allVolumes) {
      if (v.VolumeType === VolumeTypeEnum.Secured || v.VolumeType === VolumeTypeEnum.InProgress) {
        if (v.Quantity > 0) securedYears.add(v.Year);
      }
    }

    // Also include inputYear if there are any input values
    if (inputYear && Object.keys(inputValues).length > 0) {
      securedYears.add(inputYear);
    }

    return Array.from(securedYears)
      .sort()
      .map((year, i) => ({
        key: `secured_${year}`,
        label:
          year === lastYear && !activeFacilities.every((f) => isFacilityYearSecured(f, year))
            ? `Pågående ${year}`
            : `Säkrad ${year}`,
        color: SECURED_LINE_COLORS[i % SECURED_LINE_COLORS.length],
        year,
      }));
  }, [activeFacilities, inputYear, inputValues, lastYear]);

  // Auto-enable secured lines when they first appear
  const [initializedLines, setInitializedLines] = useState<Set<string>>(new Set());
  useMemo(() => {
    const newKeys = securedLines.map((l) => l.key).filter((k) => !initializedLines.has(k));
    if (newKeys.length > 0) {
      setVisibleSecured((prev) => {
        const next = new Set(prev);
        newKeys.forEach((k) => next.add(k));
        return next;
      });
      setInitializedLines((prev) => {
        const next = new Set(prev);
        newKeys.forEach((k) => next.add(k));
        return next;
      });
    }
  }, [securedLines, initializedLines]);

  const toggleSecured = useCallback((key: string) => {
    setVisibleSecured((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const chartData = useMemo(() =>
    buildMonthlyData(
      activeFacilities, activeFacilityIndices,
      currentYear, prevYear1, prevYear2, securedLines, years,
      inputYear, inputValues,
      showCurrentYear, showPrevYear1, showPrevYear2, visibleSecured,
    ),
    [activeFacilities, activeFacilityIndices, currentYear, prevYear1, prevYear2,
     securedLines, years, inputYear, inputValues,
     showCurrentYear, showPrevYear1, showPrevYear2, visibleSecured],
  );

  // Build legend from active series
  const legendItems = useMemo(() => {
    const items: { value: string; color: string; type: "square" | "line" }[] = [];
    if (showCurrentYear) {
      items.push({ value: `Förbrukning ${currentYear}`, color: VOLUME_TYPE_CONFIG[VolumeTypeEnum.Consumption].color, type: "square" });
      items.push({ value: `Prognos ${currentYear}`, color: VOLUME_TYPE_CONFIG[VolumeTypeEnum.Forecast].color, type: "square" });
    }
    if (showPrevYear1) {
      items.push({ value: `Förbrukning ${prevYear1}`, color: "#90caf9", type: "square" });
    }
    if (showPrevYear2) {
      items.push({ value: `Förbrukning ${prevYear2}`, color: "#b0bec5", type: "square" });
    }
    for (const line of securedLines) {
      if (visibleSecured.has(line.key)) {
        items.push({ value: line.label, color: line.color, type: "line" });
      }
    }
    return items;
  }, [showCurrentYear, showPrevYear1, showPrevYear2, currentYear, prevYear1, prevYear2, securedLines, visibleSecured]);

  // Tooltip labels
  const tooltipLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {
      currentConsumption: `Förbrukning ${currentYear}`,
      currentForecast: `Prognos ${currentYear}`,
      prev1Consumption: `Förbrukning ${prevYear1}`,
      prev2Consumption: `Förbrukning ${prevYear2}`,
    };
    for (const line of securedLines) {
      labels[line.key] = line.label;
    }
    return labels;
  }, [currentYear, prevYear1, prevYear2, securedLines]);

  return (
    <Box>
      {/* Facility selector – chips för få anläggningar, dropdown när de blir fler */}
      {facilities.length > 1 && facilities.length <= 2 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1}>
          <Chip
            label="Alla anläggningar"
            size="small"
            variant={selectedFacilityIndex === null ? "filled" : "outlined"}
            color={selectedFacilityIndex === null ? "primary" : "default"}
            onClick={() => setSelectedFacilityIndex(null)}
          />
          {facilities.map((f, i) => (
            <Chip
              key={f.CustomerBillingPointNo}
              label={f.Address || `Anläggning ${f.BillingPointId}`}
              size="small"
              variant={selectedFacilityIndex === i ? "filled" : "outlined"}
              color={selectedFacilityIndex === i ? "primary" : "default"}
              onClick={() => setSelectedFacilityIndex(i)}
            />
          ))}
        </Stack>
      )}

      {/* Series toggles */}
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Visa:</Typography>
        <Chip
          label={`${currentYear}`}
          size="small"
          variant={showCurrentYear ? "filled" : "outlined"}
          color={showCurrentYear ? "info" : "default"}
          onClick={() => setShowCurrentYear((v) => !v)}
        />
        <Chip
          label={`${prevYear1}`}
          size="small"
          variant={showPrevYear1 ? "filled" : "outlined"}
          color={showPrevYear1 ? "info" : "default"}
          onClick={() => setShowPrevYear1((v) => !v)}
        />
        <Chip
          label={`${prevYear2}`}
          size="small"
          variant={showPrevYear2 ? "filled" : "outlined"}
          color={showPrevYear2 ? "info" : "default"}
          onClick={() => setShowPrevYear2((v) => !v)}
        />
        {securedLines.map((line) => (
          <Chip
            key={line.key}
            label={line.label}
            size="small"
            variant={visibleSecured.has(line.key) ? "filled" : "outlined"}
            sx={{
              ...(visibleSecured.has(line.key) && {
                bgcolor: line.color,
                color: "#fff",
                "&:hover": { bgcolor: line.color, opacity: 0.85 },
              }),
            }}
            onClick={() => toggleSecured(line.key)}
          />
        ))}
        {facilities.length > 2 && (
          <TextField
            select
            size="small"
            value={selectedFacilityIndex ?? ""}
            onChange={(e) =>
              setSelectedFacilityIndex(e.target.value === "" ? null : Number(e.target.value))
            }
            sx={{
              ml: "auto",
              minWidth: 200,
              maxWidth: 280,
              "& .MuiInputBase-root": { height: 28, fontSize: "0.8rem" },
              "& .MuiSelect-select": { py: 0.25 },
            }}
            slotProps={{ select: { displayEmpty: true } }}
          >
            <MenuItem value="">Alla anläggningar ({facilities.length})</MenuItem>
            {facilities.map((f, i) => (
              <MenuItem key={f.CustomerBillingPointNo} value={i}>
                {f.Address || `Anläggning ${f.BillingPointId}`}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <Box sx={{ width: "100%", height: 250 }}>
        <ResponsiveContainer>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={(v: number) => fmt.format(v)}
            />
            <Tooltip
              formatter={(value: number, _name: string, props) => {
                if (value === 0) return null;
                const dataKey = (props as { dataKey?: string }).dataKey ?? "";
                const label = tooltipLabels[dataKey] ?? dataKey;
                return [`${fmt.format(value)} kWh`, label];
              }}
            />
            <Legend
              payload={legendItems.map((item) => ({
                value: item.value,
                color: item.color,
                type: item.type,
              }))}
              wrapperStyle={{ fontSize: 12 }}
            />

            {/* Current year bars */}
            {showCurrentYear && <Bar dataKey="currentConsumption" stackId="current" name={`Förbrukning ${currentYear}`} fill={VOLUME_TYPE_CONFIG[VolumeTypeEnum.Consumption].color} />}
            {showCurrentYear && <Bar dataKey="currentForecast" stackId="current" name={`Prognos ${currentYear}`} fill={VOLUME_TYPE_CONFIG[VolumeTypeEnum.Forecast].color} radius={[2, 2, 0, 0]} />}

            {/* Previous year bars */}
            {showPrevYear1 && <Bar dataKey="prev1Consumption" stackId="prev1" name={`Förbrukning ${prevYear1}`} fill="#90caf9" radius={[2, 2, 0, 0]} />}
            {showPrevYear2 && <Bar dataKey="prev2Consumption" stackId="prev2" name={`Förbrukning ${prevYear2}`} fill="#b0bec5" radius={[2, 2, 0, 0]} />}

            {/* Secured / InProgress lines */}
            {securedLines.map((line) =>
              visibleSecured.has(line.key) ? (
                <Line
                  key={line.key}
                  dataKey={line.key}
                  type="monotone"
                  name={line.label}
                  stroke={line.color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: line.color }}
                  activeDot={{ r: 5 }}
                />
              ) : null,
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
